import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FileText, Clock, CheckCircle2, RefreshCw, XCircle, Trash2, Edit3, Calendar } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

export default function MyApplications() {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduleModal, setScheduleModal] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({ date: '', time: '' });

  useEffect(() => {
    const fetchAll = async () => {
      const token = localStorage.getItem('userToken');
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const [appsRes, draftsRes, visitsRes] = await Promise.all([
          axios.get(`${API_URL}/api/my-applications`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/api/drafts`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/visits/my`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
        ]);
        
        const myApps = Array.isArray(appsRes.data) ? appsRes.data : [];
        const myDrafts = Array.isArray(draftsRes.data) ? draftsRes.data : [];
        const myVisits = Array.isArray(visitsRes.data) ? visitsRes.data : [];
        
        setApplications(myApps);
        setDrafts(myDrafts);
        setVisits(myVisits);
        
        // Auto-resume trigger via URL query param
        const searchParams = new URLSearchParams(window.location.search);
        const resumeId = searchParams.get('resume');
        if (resumeId) {
           const targetApp = myApps.find(a => String(a.id || a._id) === resumeId) || myDrafts.find(d => String(d.id || d._id) === resumeId);
           if (targetApp) {
              // Same as clicking "Complete Application"
              navigate(`/category/${encodeURIComponent(targetApp.category_name)}?app=${targetApp.application_number || targetApp.id || targetApp._id}&preview=true`);
           }
        }
        
      } catch (err) {
        console.error(err);
        toast.error('માહિતી લાવવામાં નિષ્ફળતા');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [navigate]);

  const handleDeleteDraft = async (id) => {
    if (!window.confirm('શું આ ડ્રાફ્ટ કાઢી નાખવો છે?')) return;
    const token = localStorage.getItem('userToken');
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.delete(`${API_URL}/api/drafts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setDrafts(prev => prev.filter(d => d.id !== id));
      toast.success('ડ્રાફ્ટ કાઢી નાખ્યો');
    } catch (e) {
      toast.error('ડ્રાફ્ટ કાઢી નાખવામાં નિષ્ફળ');
    }
  };

  const handleContinueDraft = (d) => {
    // Navigate to the sub-category page with the draft id so it auto-loads
    navigate(`/category/${encodeURIComponent(d.category_name)}?draft=${d.id}`);
  };
  
  const handleCompleteApplication = (app) => {
    // Navigate to sub-category page with app param so it auto-loads and directly goes to Preview/Payment
    navigate(`/category/${encodeURIComponent(app.category_name)}?app=${app.application_number}&preview=true`);
  };

  const token = localStorage.getItem('userToken');

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] font-sans flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center p-4">
          <div className="bg-white p-8 rounded-xl shadow-sm border border-[#E2E8F0] text-center max-w-md">
            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4 text-[#172554]">
              <FileText className="w-8 h-8" />
            </div>
            <h2 className="text-xl font-bold text-[#172554] text-gujarati mb-2">લૉગિન જરૂરી છે</h2>
            <p className="text-[#64748B] text-sm text-gujarati mb-6">
              તમારી અરજીઓ જોવા માટે કૃપા કરીને ઉપર જમણી બાજુએથી લૉગિન કરો.
            </p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-sans flex flex-col">
      <Header />
      <main className="flex-1 px-4 md:px-24 py-8 w-full max-w-5xl mx-auto">
        <h2 className="text-2xl font-bold text-[#172554] text-gujarati mb-6 border-b-2 border-[#F5B800] pb-2 inline-block">
          મારી અરજીઓ (My Applications)
        </h2>
        
        {loading ? (
          <div className="py-10 text-center text-[#172554]">Loading...</div>
        ) : (
          <>
            {/* Drafts section — half-filled forms that can be resumed */}
            {drafts.length > 0 && (
              <div className="mb-6" data-testid="drafts-section">
                <h3 className="text-lg font-bold text-[#172554] text-gujarati mb-3 flex items-center gap-2">
                  <Edit3 className="w-5 h-5 text-[#F5B800]" />
                  અધુરી અરજીઓ (Drafts) · {drafts.length}
                </h3>
                <div className="flex flex-col gap-3">
                  {drafts.map(d => (
                    <div key={d.id} className="bg-[#FFF8DB] p-4 md:p-5 rounded-lg shadow-sm border-l-4 border-[#F5B800] flex flex-col md:flex-row justify-between md:items-center gap-3" data-testid={`draft-row-${d.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] uppercase tracking-wider text-[#F5B800] font-extrabold bg-[#172554] px-2 py-0.5 rounded">DRAFT</span>
                          {d.application_type === 'Urgent' && (
                            <span className="inline-flex items-center gap-1 bg-gradient-to-r from-[#F5B800] to-[#FFC928] text-[#172554] px-2 py-0.5 rounded-full text-[10px] font-extrabold text-gujarati">તત્કાલ</span>
                          )}
                          <span className="text-[10px] text-[#64748B]">
                            છેલ્લે સેવ: {d.updated_at ? new Date(d.updated_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                        </div>
                        <div className="font-bold text-[#172554] text-gujarati text-base mt-1">{d.sub_category_name}</div>
                        <div className="text-xs text-[#64748B] text-gujarati">{d.category_name}</div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button
                          onClick={() => handleContinueDraft(d)}
                          className="bg-[#F5B800] hover:bg-[#FFC928] text-[#172554] font-bold py-2 px-4 rounded text-sm text-gujarati"
                          data-testid={`draft-continue-${d.id}`}
                        >
                          ચાલુ રાખો
                        </button>
                        <button
                          onClick={() => handleDeleteDraft(d.id)}
                          className="border border-red-300 text-red-500 hover:bg-red-50 p-2 rounded"
                          data-testid={`draft-delete-${d.id}`}
                          title="Delete draft"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {applications.length === 0 && drafts.length === 0 ? (
              <div className="bg-white p-8 rounded-lg shadow-sm border border-[#E2E8F0] text-center text-[#64748B] text-gujarati">
                કોઈ અરજીઓ મળી નથી.
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {applications.map(app => (
              <div key={app.id} className="bg-white p-4 md:p-6 rounded-lg shadow-sm border border-[#E2E8F0] hover:shadow-md transition-shadow">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-bold text-[#172554] text-lg">{app.application_number}</span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                      {new Date(app.created_at).toLocaleDateString('en-IN')}
                    </span>
                    {app.application_type && app.application_type.includes('Urgent') && (
                      <span 
                        data-testid={`urgent-ribbon-${app.application_number}`}
                        className="inline-flex items-center gap-1 bg-gradient-to-r from-[#F5B800] to-[#FFC928] text-[#172554] px-2.5 py-0.5 rounded-full text-[11px] font-extrabold text-gujarati shadow-sm ring-1 ring-[#F5B800]/50 uppercase tracking-wide"
                        title="તત્કાલ અરજી (Urgent)"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3"><path d="M13 2 3 14h7l-1 8 10-12h-7l1-8Z"/></svg>
                        તત્કાલ
                      </span>
                    )}
                  </div>
                  <div className="font-bold text-[#172554] text-gujarati text-lg">
                    {app.sub_category_name}
                  </div>
                  <div className="text-sm text-[#64748B] text-gujarati">
                    {app.category_name}
                  </div>
                </div>
                
                <div className="flex flex-col items-start md:items-end gap-3 w-full md:w-auto mt-2 md:mt-0">
                  <div className="flex items-center gap-2">
                    {(app.status === 'Draft' || app.status === 'payment_pending') && <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-bold text-gujarati flex items-center gap-1"><XCircle className="w-3 h-3"/> ચુકવણી બાકી (Payment Pending)</span>}
                    {app.status === 'Submitted' && <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-bold text-gujarati flex items-center gap-1"><Clock className="w-3 h-3"/> સબમિટ થઈ ગઈ</span>}
                    {app.status === 'Pending' && <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-xs font-bold text-gujarati flex items-center gap-1"><RefreshCw className="w-3 h-3"/> પેન્ડિંગ (Pending)</span>}
                    {app.status === 'Inprocess' && <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-xs font-bold text-gujarati flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin"/> પ્રક્રિયા ચાલુ છે (In Process)</span>}
                    {app.status === 'Complete' && <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold text-gujarati flex items-center gap-1"><CheckCircle2 className="w-3 h-3"/> પૂર્ણ (Complete)</span>}
                    {app.status === 'Cancelled' && <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-bold text-gujarati flex items-center gap-1 ring-1 ring-red-300"><XCircle className="w-3 h-3"/> રદ / રિફંડ</span>}
                  </div>
                  
                  {(app.status === 'Draft' || app.status === 'payment_pending') ? (
                    <button 
                      onClick={() => handleCompleteApplication(app)}
                      className="w-full md:w-auto bg-red-600 hover:bg-red-700 text-white font-bold py-1.5 px-4 rounded text-sm transition-colors text-gujarati"
                    >
                      અરજી પૂર્ણ કરો (Complete Application)
                    </button>
                  ) : (
                    <>
                      <button 
                        onClick={() => navigate(`/track/${app.application_number}`)}
                        className="w-full md:w-auto bg-white border border-[#172554] text-[#172554] font-bold py-1.5 px-4 rounded text-sm hover:bg-gray-50 transition-colors text-gujarati"
                      >
                        સ્થિતિ તપાસો
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const API_URL = process.env.REACT_APP_BACKEND_URL;
                            toast.info('અરજી ફોર્મ તૈયાર થઈ રહ્યું છે... (Preparing)');
                            const res = await axios.get(
                              `${API_URL}/api/applications/${app.application_number}/pack`,
                              { responseType: 'blob' }
                            );
                            const client = (app.full_name || app.user_phone || 'Client').replace(/[^A-Za-z0-9\u0A80-\u0AFF_-]+/g, '_') || 'Client';
                            const blob = new Blob([res.data], { type: 'application/pdf' });
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `${app.application_number}_${client}_Complete_Application.pdf`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            setTimeout(() => URL.revokeObjectURL(url), 4000);
                          } catch (e) {
                            console.error(e);
                            toast.error('અરજી ફોર્મ ડાઉનલોડ કરવામાં નિષ્ફળ');
                          }
                        }}
                        className="w-full md:w-auto bg-[#F5B800] hover:bg-[#FFC928] text-[#172554] font-extrabold py-1.5 px-4 rounded text-sm text-gujarati flex items-center justify-center gap-1.5 shadow-sm"
                        data-testid={`my-apps-download-pack-${app.application_number}`}
                        title="Download Complete Application PDF (form + documents + receipt)"
                      >
                        <FileText className="w-3.5 h-3.5" /> ડાઉનલોડ અરજી ફોર્મ
                      </button>
                    </>
                  )}
                  {(() => {
                    const visit = visits.find(v => v.application_number === app.application_number);
                    if (visit && visit.status === 'Pending Schedule') {
                      return (
                        <button 
                          onClick={() => { setScheduleModal(visit); setScheduleForm({ date: '', time: '' }); }}
                          className="w-full md:w-auto bg-green-600 hover:bg-green-700 text-white font-bold py-1.5 px-4 rounded text-sm text-gujarati flex items-center justify-center gap-1.5 shadow-sm"
                        >
                          <Calendar className="w-3.5 h-3.5" /> મુલાકાત શેડ્યૂલ કરો
                        </button>
                      );
                    } else if (visit) {
                      return (
                        <span className="w-full md:w-auto bg-green-50 text-green-700 font-bold py-1.5 px-4 rounded text-sm text-gujarati flex items-center justify-center gap-1.5 border border-green-200">
                          <Calendar className="w-3.5 h-3.5" /> 
                          {visit.status === 'Scheduled' ? `મુલાકાત: ${new Date(visit.scheduled_date).toLocaleDateString()} at ${visit.scheduled_time}` : `મુલાકાત: ${visit.status}`}
                        </span>
                      );
                    }
                    return null;
                  })()}
                </div>
                </div>

                {app.assigned_executive?.name && (
                  <div className="mt-3 border-t border-gray-100 pt-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`my-apps-exec-card-${app.application_number}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-[#F5B800]/20 flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-[#172554]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-bold">Assigned Executive</div>
                        <div className="font-bold text-[#172554] text-gujarati text-sm">{app.assigned_executive.name}</div>
                        <div className="text-xs text-[#64748B] text-gujarati">{app.assigned_executive.designation}</div>
                      </div>
                    </div>
                    {app.assigned_executive.contact_number && (
                      <a
                        href={`tel:${app.assigned_executive.contact_number}`}
                        className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold px-4 py-2 rounded text-sm transition-colors"
                        data-testid={`my-apps-call-exec-${app.application_number}`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                        Call · {app.assigned_executive.contact_number}
                      </a>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
            )}
          </>
        )}

        {scheduleModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="schedule-visit-modal">
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md relative">
              <button className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" onClick={() => setScheduleModal(null)}>
                <XCircle className="w-5 h-5" />
              </button>
              <h3 className="text-xl font-bold text-[#172554] mb-4 text-gujarati border-b pb-3">મુલાકાત શેડ્યૂલ કરો</h3>
              <p className="text-sm text-gray-600 mb-4 text-gujarati">
                ફ્રી મુલાકાત માટે તારીખ અને સમય પસંદ કરો.
              </p>
              <form onSubmit={async (e) => {
                e.preventDefault();
                if (!scheduleForm.date || !scheduleForm.time) {
                  toast.error('તારીખ અને સમય બંને પસંદ કરો');
                  return;
                }
                try {
                  const API_URL = process.env.REACT_APP_BACKEND_URL;
                  const token = localStorage.getItem('userToken');
                  await axios.post(`${API_URL}/api/visits/${scheduleModal._id || scheduleModal.id}/schedule`, {
                    scheduled_date: scheduleForm.date,
                    scheduled_time: scheduleForm.time
                  }, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success('મુલાકાત શેડ્યૂલ થઈ ગઈ');
                  setScheduleModal(null);
                  
                  // refresh visits
                  const vRes = await axios.get(`${API_URL}/api/visits/my`, { headers: { Authorization: `Bearer ${token}` } });
                  setVisits(Array.isArray(vRes.data) ? vRes.data : []);
                } catch(err) {
                  toast.error('ભૂલ આવી છે');
                }
              }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">તારીખ (Date) *</label>
                    <input type="date" required className="w-full border border-gray-300 rounded p-2.5 outline-none focus:border-green-500" min={new Date().toISOString().split('T')[0]} value={scheduleForm.date} onChange={e => setScheduleForm({...scheduleForm, date: e.target.value})} />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">સમય (Time) *</label>
                    <input type="time" required className="w-full border border-gray-300 rounded p-2.5 outline-none focus:border-green-500" value={scheduleForm.time} onChange={e => setScheduleForm({...scheduleForm, time: e.target.value})} />
                  </div>
                  <button type="submit" className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-lg text-gujarati transition-colors">શેડ્યૂલ સેવ કરો</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
